import { useFrameworkStore } from "@/stores/framework";

export type EmissionProtocol = "iso" | "ghg";

/**
 * Browser-side view of the reporting framework.
 *
 * The authority is `companypreferences.reports.framework`, read server-side
 * (plan 0.4/6.1) and mirrored into the framework store by `usePreferences`
 * (plan 6.2). This module only *reads* that store so the ~47 call sites that
 * already depend on `getProtocol()` stay synchronous - most of them are plain
 * API/hook helpers that run outside React and cannot subscribe to a store.
 *
 * `localStorage` is a pre-hydration seed only. It is never allowed to win once
 * the server has answered, because a stale browser value is exactly what files
 * emissions against the wrong taxonomy and produces a wrongly-grouped
 * inventory.
 */
export function getProtocol(): EmissionProtocol {
  return useFrameworkStore.getState().framework;
}

/**
 * Subscribing counterpart to {@link getProtocol}, for anything that *renders*
 * the framework. Callers using this re-render when hydration lands, which is
 * what keeps the UI from showing ISO until the server says otherwise.
 */
export function useProtocol(): EmissionProtocol {
  return useFrameworkStore((state) => state.framework);
}

export function categoryTerm(): "Category" | "Scope" {
  return getProtocol() === "ghg" ? "Scope" : "Category";
}
